import {
  handleErrorToast,
  handleSuccessToast,
} from 'src/components/molecules/Toast/Toast';
import { FormType } from 'src/components/pages/CoiFormManagement/types';
import { QuestionnaireStatus } from 'src/graphql';
import { countAddedQuestionsForPublish } from '../components/Header/helpers/formSubmitHelpers';
import { mapFormToQuestionnaireInput } from '../mappers/mapFormToQuestionnaireInput';
import { mapFormToUpdateQuestionnaireInput } from '../mappers/mapFormToUpdateQuestionnaireInput';
import { mapQuestionsToCreateInput } from '../mappers/mapQuestionsToCreateInput';
import { FORM_ACTIONS, QuestionData } from '../types';
import { ensureFormDraftTranslationsBeforeSave } from './ensureFormDraftTranslationsBeforeSave';
import type { HandleQuestionnaireSubmitParams } from './handleQuestionnaireSubmitTypes';
import { normalizeCountryValues } from './normalizeCountryCodes';
import { uniqueFormPublishLocaleLabels } from './formPublishTranslationValidation';
import { stripUiOnlyFields } from './stripUiOnlyFields';
import { validateAndTrimForm } from './validateAndTrimForm';
import { stripHtml } from './validation';

export const handleQuestionnaireSubmit = async (
  params: HandleQuestionnaireSubmitParams,
): Promise<void> => {
  const { action } = params;

  if (!params.formType) return;

  // Check the original selection before any filtering
  const hasGlobalCountry = (params.selectedCountry ?? []).some(
    (country) => country.value.trim().toUpperCase() === 'GLOBAL',
  );

  const selectedCountries = hasGlobalCountry
    ? [{ countryCode: 'GLOBAL', countryName: 'Global' }]
    : (params.selectedCountry ?? [])
        .map((country) => ({
          countryCode: country.value.trim().toUpperCase(),
          countryName: country.label.trim() || country.value.trim(),
        }))
        .filter((country) => Boolean(country.countryCode));

  const selectedCountryCodes = selectedCountries.map(
    (country) => country.countryCode,
  );

  if (
    action === 'publish' &&
    countAddedQuestionsForPublish(params.questions) === 0
  ) {
    return;
  }

  const isSpeakUpForm = params.formType === FormType.SPEAK_UP;
  const isDisclosureForm =
    params.formType === FormType.CONFLICT_OF_INTEREST || isSpeakUpForm;

  if (action !== 'preview') {
    params.setCurrentAction(action);
  }

  let formActionForValidation:
    | typeof FORM_ACTIONS.DRAFT
    | typeof FORM_ACTIONS.PUBLISH
    | typeof FORM_ACTIONS.PREVIEW;
  if (action === 'draft') {
    formActionForValidation = FORM_ACTIONS.DRAFT;
  } else if (action === 'publish') {
    formActionForValidation = FORM_ACTIONS.PUBLISH;
  } else {
    formActionForValidation = FORM_ACTIONS.PREVIEW;
  }

  let questionsForValidation = params.questions;
  let mergedTitleTranslations = { ...params.titleTranslations };
  let mergedDescriptionTranslations = { ...params.descriptionTranslations };

  // Ensure all selected languages have translations before saving (applies to both SpeakUp and COI)
  if (
    (action === 'draft' || action === 'publish') &&
    params.translateFormDraftContent
  ) {
    try {
      const {
        questions: translatedQuestions,
        titleTranslations: autoTitleTranslations,
        descriptionTranslations: autoDescriptionTranslations,
      } = await ensureFormDraftTranslationsBeforeSave({
        questions: params.questions,
        formTitle: params.title,
        formDescription: params.description,
        selectedLanguages: params.selectedLanguages,
        translate: async (input) => {
          const data = await params.translateFormDraftContent!({
            input,
          });
          return data.translateFormDraftContent;
        },
      });
      questionsForValidation = translatedQuestions;
      params.setQuestions(translatedQuestions);

      // Merge auto-translated values with existing manual translations
      mergedTitleTranslations = {
        ...mergedTitleTranslations,
        ...autoTitleTranslations,
      };
      mergedDescriptionTranslations = {
        ...mergedDescriptionTranslations,
        ...autoDescriptionTranslations,
      };

      // Update state for future edits
      if (Object.keys(autoTitleTranslations).length > 0) {
        params.setTitleTranslations((prev) => ({
          ...prev,
          ...autoTitleTranslations,
        }));
      }
      if (Object.keys(autoDescriptionTranslations).length > 0) {
        params.setDescriptionTranslations((prev) => ({
          ...prev,
          ...autoDescriptionTranslations,
        }));
      }
    } catch {
      params.setCurrentAction(null);
      handleErrorToast({
        toastMessage:
          action === 'draft' ? "Couldn't save draft" : "Couldn't publish form",
        toastDescription:
          'Automatic translation failed. Try again or open each language under Language variants to translate manually.',
      });
      return;
    }
  }

  const validationResult = validateAndTrimForm({
    isSpeakUpForm,
    title: params.title,
    description: params.description,
    categoryName: params.categoryName,
    categoryDescription: params.categoryDescription,
    questions: questionsForValidation,
    selectedCountryValue: params.selectedCountry?.[0]?.value,
    selectedCountries: params.selectedCountry,
    action: formActionForValidation,
    selectedLanguages: params.selectedLanguages,
    setTitle: params.setTitle,
    setDescription: params.setDescription,
    setCategoryName: params.setCategoryName,
    setCategoryDescription: params.setCategoryDescription,
    setQuestions: params.setQuestions,
    setTitleTouched: params.setTitleTouched,
    setDescriptionTouched: params.setDescriptionTouched,
    setCategoryNameTouched: params.setCategoryNameTouched,
    setCategoryDescriptionTouched: params.setCategoryDescriptionTouched,
    setCountryTouched: params.setCountryTouched,
    titleTouched: params.titleTouched,
    descriptionTouched: params.descriptionTouched,
    categoryNameTouched: params.categoryNameTouched,
    categoryDescriptionTouched: params.categoryDescriptionTouched,
    countryTouched: params.countryTouched,
  });

  if (!validationResult.isValid) {
    if (action !== 'preview') {
      params.setCurrentAction(null);
    }

    let toastMessage: string;
    if (action === 'draft') {
      toastMessage = "Couldn't save draft";
    } else if (action === 'publish') {
      toastMessage = "Couldn't publish form";
    } else {
      toastMessage = 'Cannot preview';
    }

    let toastDescription =
      action === 'preview'
        ? 'Form contains errors. Please fix all errors before previewing.'
        : 'Form contains errors. Please fix all errors before ' +
          (action === 'draft' ? 'saving.' : 'publishing.');
    if (
      isSpeakUpForm &&
      validationResult.formPublishTranslationIssues.length > 0
    ) {
      const labels = uniqueFormPublishLocaleLabels(
        validationResult.formPublishTranslationIssues,
      );
      toastDescription += ` Translation errors in: ${labels.join(
        ', ',
      )}. Please amend the text to fit within the character limit.`;

      const firstIssue = validationResult.formPublishTranslationIssues[0];
      if (firstIssue && params.setActiveEditingLocale) {
        setTimeout(() => {
          params.setActiveEditingLocale!(firstIssue.localeValue);
        }, 0);
      }
    }

    handleErrorToast({
      toastMessage,
      toastDescription,
    });

    setTimeout(() => {
      const errorQuestionIds = new Set([
        ...validationResult.questionsWithInvalidText.map(
          (q: QuestionData) => q.id,
        ),
        ...validationResult.questionsWithInvalidDescription.map(
          (q: QuestionData) => q.id,
        ),
        ...validationResult.questionsWithoutAnswerType.map(
          (q: QuestionData) => q.id,
        ),
        ...validationResult.questionsWithEmptyOptions.map(
          (q: QuestionData) => q.id,
        ),
        ...validationResult.questionsWithInvalidOptions.map(
          (q: QuestionData) => q.id,
        ),
        ...validationResult.questionsWithMissingDatabaseField.map(
          (q: QuestionData) => q.id,
        ),
        ...validationResult.formPublishTranslationIssues.map(
          (issue) => issue.questionId,
        ),
      ]);

      const firstErrorQuestion = validationResult.trimmedQuestions.find((q) =>
        errorQuestionIds.has(q.id),
      );

      if (firstErrorQuestion) {
        params.questionRefs.current
          ?.get(firstErrorQuestion.id)
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
      }
    }, 10);

    return;
  }

  const trimmedQuestionsForSave = validationResult.trimmedQuestions;

  // If preview action, open preview modal and return
  if (action === 'preview') {
    if (params.setIsPreviewOpen) {
      params.setIsPreviewOpen(true);
    }
    return;
  }

  const handleError = () => {
    params.setCurrentAction(null);
    if (action === 'draft') {
      handleErrorToast({
        toastMessage: "Couldn't save draft",
        toastDescription: "We couldn't save your draft. Try again.",
      });
    } else {
      handleErrorToast({
        toastMessage: "Couldn't publish form",
        toastDescription: "We couldn't publish your form. Try again.",
      });
    }
  };

  const handleDraftSuccess = (
    idOrShouldUpdateRef?: string | boolean,
    shouldUpdateRef = true,
  ) => {
    params.setCurrentAction(null);
    const id =
      typeof idOrShouldUpdateRef === 'string' ? idOrShouldUpdateRef : undefined;
    const shouldUpdate =
      typeof idOrShouldUpdateRef === 'boolean'
        ? idOrShouldUpdateRef
        : shouldUpdateRef;

    if (shouldUpdate) {
      const trimmedCategoryDescription = stripHtml(
        validationResult.trimmedCategoryDescription,
      ).trim();
      const trimmedDescription = stripHtml(
        validationResult.trimmedDescription,
      ).trim();
      params.lastSavedStateRef.current = {
        title: validationResult.trimmedTitle,
        description: trimmedDescription,
        categoryName: validationResult.trimmedCategoryName,
        categoryDescription: trimmedCategoryDescription,
        questions: JSON.parse(JSON.stringify(trimmedQuestionsForSave)),
        selectedLanguages: params.selectedLanguages,
        selectedCountryCodes,
        autoReviewEnabled: params.autoReviewEnabled,
      };
    }
    params.invalidateFormCache(id);
    handleSuccessToast({
      toastMessage: 'Draft saved',
      toastDescription: 'Your changes are saved. The form is not public yet.',
    });
  };

  const handlePublishSuccess = (id?: string) => {
    params.setCurrentAction(null);
    const trimmedCategoryDescription = stripHtml(
      validationResult.trimmedCategoryDescription,
    ).trim();
    const trimmedDescription = stripHtml(
      validationResult.trimmedDescription,
    ).trim();
    params.lastSavedStateRef.current = {
      title: validationResult.trimmedTitle,
      description: trimmedDescription,
      categoryName: validationResult.trimmedCategoryName,
      categoryDescription: trimmedCategoryDescription,
      questions: JSON.parse(JSON.stringify(trimmedQuestionsForSave)),
      selectedLanguages: params.selectedLanguages,
      selectedCountryCodes,
      autoReviewEnabled: params.autoReviewEnabled,
    };
    params.invalidateFormCache(id);
    params.setPreventNavigation(false);
    handleSuccessToast({
      toastMessage: 'Form published',
      toastDescription:
        'The form is now public. Users can create and submit reports using this form.',
    });
    params.history.push(params.returnTo ?? '/form-management');
  };

  if (params.questionnaireId) {
    const questionnaireId = params.questionnaireId;

    const saved = params.lastSavedStateRef.current;
    const languagesChanged =
      JSON.stringify(params.selectedLanguages) !==
      JSON.stringify(saved.selectedLanguages);
    const countriesChanged =
      normalizeCountryValues(params.selectedCountry) !==
      normalizeCountryValues(
        saved.selectedCountryCodes.map((code) => ({
          value: code,
          label: code,
        })),
      );

    const commonQuestionnaireChanges =
      validationResult.trimmedTitle !== saved.title ||
      stripHtml(validationResult.trimmedDescription).trim() !==
        saved.description ||
      validationResult.trimmedCategoryName !== saved.categoryName ||
      stripHtml(validationResult.trimmedCategoryDescription).trim() !==
        saved.categoryDescription ||
      languagesChanged ||
      countriesChanged;

    const questionnaireChanged = isSpeakUpForm
      ? commonQuestionnaireChanges
      : commonQuestionnaireChanges ||
        params.autoReviewEnabled !== saved.autoReviewEnabled;

    const questionsChanged =
      JSON.stringify(stripUiOnlyFields(trimmedQuestionsForSave)) !==
      JSON.stringify(stripUiOnlyFields(saved.questions));

    const handleQuestionsUpdate = (onDone: () => void) => {
      if (!questionsChanged) {
        onDone();
        return;
      }

      const allQuestions = mapQuestionsToCreateInput(trimmedQuestionsForSave, {
        isDisclosureForm,
      });

      params.bulkCreateQuestions(
        {
          questionnaireId: questionnaireId,
          input: { questions: allQuestions },
        },
        {
          onSuccess: (questionData) => {
            const response = questionData.bulkCreateFormManagementQuestions;
            const upsertedQuestions = response?.questions || [];

            params.setQuestions(
              upsertedQuestions.map((q, index: number) => {
                const originalQuestion = trimmedQuestionsForSave[index];
                return {
                  ...originalQuestion,
                  id: q.id || originalQuestion.id,
                };
              }),
            );

            if (action === 'draft') {
              const trimmedCategoryDescription = stripHtml(
                validationResult.trimmedCategoryDescription,
              ).trim();
              const trimmedDescription = stripHtml(
                validationResult.trimmedDescription,
              ).trim();
              params.lastSavedStateRef.current = {
                title: validationResult.trimmedTitle,
                description: trimmedDescription,
                categoryName: validationResult.trimmedCategoryName,
                categoryDescription: trimmedCategoryDescription,
                questions: JSON.parse(
                  JSON.stringify(
                    upsertedQuestions.map((q, index: number) => {
                      const originalQuestion = trimmedQuestionsForSave[index];
                      return {
                        ...originalQuestion,
                        id: q.id || originalQuestion.id,
                      };
                    }),
                  ),
                ),
                selectedLanguages: params.selectedLanguages,
                selectedCountryCodes,
                autoReviewEnabled: params.autoReviewEnabled,
              };
            }

            onDone();
          },
          onError: () => {
            params.setCurrentAction(null);
            handleErrorToast({
              toastMessage: 'Failed to save questions',
              toastDescription:
                'The form was saved, but questions could not be saved. Please try editing the form again.',
            });
          },
        },
      );
    };

    const handleAfterUpdates = () => {
      if (action === 'publish') {
        if (params.isActiveForm) {
          handlePublishSuccess();
        } else {
          params.updateQuestionnaireStatus(
            {
              id: questionnaireId,
              input: { status: QuestionnaireStatus.Active },
            },
            {
              onSuccess: () => handlePublishSuccess(),
              onError: handleError,
            },
          );
        }
      } else {
        const shouldUpdateRef = !questionsChanged;
        handleDraftSuccess(shouldUpdateRef);
      }
    };

    if (questionnaireChanged) {
      const updateInput = mapFormToUpdateQuestionnaireInput(
        validationResult.trimmedTitle,
        validationResult.trimmedDescription,
        validationResult.trimmedCategoryName,
        params.categoryId,
        params.selectedLanguages,
        params.autoReviewEnabled,
        isSpeakUpForm,
        selectedCountries,
        mergedTitleTranslations,
        mergedDescriptionTranslations,
      );

      params.updateQuestionnaire(
        { id: questionnaireId, input: updateInput },
        {
          onSuccess: () => handleQuestionsUpdate(handleAfterUpdates),
          onError: handleError,
        },
      );
    } else {
      handleQuestionsUpdate(handleAfterUpdates);
    }
  } else {
    const input = mapFormToQuestionnaireInput(
      validationResult.trimmedTitle,
      validationResult.trimmedDescription,
      params.selectedLanguages,
      params.formType === FormType.SPEAK_UP
        ? 'speak-up'
        : 'conflict-of-interest',
      validationResult.trimmedCategoryName,
      params.categoryId,
      params.formCategoryMap,
      params.autoReviewEnabled,
      action === 'publish' ? 'active' : undefined,
      selectedCountries,
      mergedTitleTranslations,
      mergedDescriptionTranslations,
    );

    const handleCreateSuccess = (data: {
      createFormManagementQuestionnaire?: { id?: string | null } | null;
    }) => {
      const createdId = data.createFormManagementQuestionnaire?.id;

      if (!createdId) {
        params.setCurrentAction(null);
        handleErrorToast({
          toastMessage: "Couldn't save draft",
          toastDescription: "We couldn't save your draft. Try again.",
        });
        return;
      }

      params.setQuestionnaireId(createdId);

      const newQuestions = mapQuestionsToCreateInput(trimmedQuestionsForSave, {
        omitQuestionIds: false, // Keep temp IDs for branching logic mapping
        isDisclosureForm,
      });

      if (newQuestions.length > 0) {
        params.bulkCreateQuestions(
          {
            questionnaireId: createdId,
            input: { questions: newQuestions },
          },
          {
            onSuccess: (questionData) => {
              const createdQuestions =
                questionData.bulkCreateFormManagementQuestions?.questions || [];

              params.setQuestions(() => {
                const updatedQuestions = trimmedQuestionsForSave.map((q, i) => {
                  const createdQ = createdQuestions[i];
                  if (createdQ?.id) {
                    // Update question ID and options with actual saved data
                    const updatedOptions = q.options?.map((opt, optIndex) => {
                      const createdOpt = createdQ.options?.[optIndex];
                      if (createdOpt) {
                        // Map backend IDs back to frontend format
                        let nextAction = opt.nextAction;
                        if (createdOpt.targetQuestionId) {
                          nextAction = `question:${createdOpt.targetQuestionId}`;
                        } else if (
                          createdOpt.navigationAction === 'submit_form'
                        ) {
                          nextAction = 'submit';
                        } else if (
                          createdOpt.navigationAction === 'next_question'
                        ) {
                          nextAction = 'next';
                        }
                        return { ...opt, nextAction };
                      }
                      return opt;
                    });
                    return { ...q, id: createdQ.id, options: updatedOptions };
                  }
                  return q;
                });

                if (action === 'draft') {
                  const trimmedCategoryDescription = stripHtml(
                    validationResult.trimmedCategoryDescription,
                  ).trim();
                  const trimmedDescription = stripHtml(
                    validationResult.trimmedDescription,
                  ).trim();
                  params.lastSavedStateRef.current = {
                    title: validationResult.trimmedTitle,
                    description: trimmedDescription,
                    categoryName: validationResult.trimmedCategoryName,
                    categoryDescription: trimmedCategoryDescription,
                    questions: JSON.parse(JSON.stringify(updatedQuestions)),
                    selectedLanguages: params.selectedLanguages,
                    selectedCountryCodes,
                    autoReviewEnabled: params.autoReviewEnabled,
                  };
                }

                return updatedQuestions;
              });

              if (action === 'draft') {
                handleDraftSuccess(createdId, false);
              } else {
                handlePublishSuccess(createdId);
              }
            },
            onError: () => {
              params.setCurrentAction(null);
              handleErrorToast({
                toastMessage: 'Failed to create questions',
                toastDescription:
                  'The form was saved, but questions could not be created. Please try editing the form and adding questions again.',
              });
            },
          },
        );
      } else {
        if (action === 'draft') {
          handleDraftSuccess(createdId);
        } else {
          handlePublishSuccess(createdId);
        }
      }
    };

    params.createQuestionnaire(
      { input },
      {
        onSuccess: handleCreateSuccess,
        onError: handleError,
      },
    );
  }
};
